import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { FolderOpen, Download, FileText, Video, Image } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const StudentMaterials = () => {
  const { token } = useAuth();
  const [materials, setMaterials] = useState([]);
  const [enrollments, setEnrollments] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const headers = { Authorization: `Bearer ${token}` };
        const [materialsRes, enrollmentsRes] = await Promise.all([
          axios.get(`${API_URL}/api/study-materials`),
          axios.get(`${API_URL}/api/enrollments/my`, { headers })
        ]);
        setMaterials(materialsRes.data);
        setEnrollments(enrollmentsRes.data);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [token]);

  const enrolledCourseIds = enrollments.map(e => e.course_id);
  const myMaterials = materials.filter(m => enrolledCourseIds.includes(m.course_id));

  const getIcon = (url) => {
    if (url?.includes('video')) return Video;
    if (url?.includes('image')) return Image;
    return FileText;
  };

  return (
    <DashboardLayout title="Study Materials">
      {loading ? (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <Card key={i} className="animate-pulse">
              <CardContent className="h-32"></CardContent>
            </Card>
          ))}
        </div>
      ) : myMaterials.length > 0 ? (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {myMaterials.map((material) => {
            const Icon = getIcon(material.file_url);
            return (
              <Card key={material.id} className="card-hover" data-testid={`material-${material.id}`}>
                <CardContent className="p-4">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                      <Icon className="w-6 h-6 text-primary" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="font-semibold text-sm truncate">{material.title}</h4>
                      {material.subject && (
                        <Badge variant="secondary" className="mt-1">{material.subject}</Badge>
                      )}
                      {material.description && (
                        <p className="text-xs text-muted-foreground mt-2 line-clamp-2">{material.description}</p>
                      )}
                    </div>
                  </div>
                  <Button size="sm" className="w-full mt-4" asChild>
                    <a href={material.file_url} target="_blank" rel="noopener noreferrer">
                      <Download className="w-4 h-4 mr-2" />
                      Download
                    </a>
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>
      ) : (
        <Card>
          <CardContent className="py-16 text-center">
            <FolderOpen className="w-16 h-16 mx-auto text-muted-foreground/30 mb-4" />
            <h3 className="font-heading text-xl font-semibold mb-2">No Materials Available</h3>
            <p className="text-muted-foreground">Study materials for your enrolled courses will appear here.</p>
          </CardContent>
        </Card>
      )}
    </DashboardLayout>
  );
};

export default StudentMaterials;
